import {
    getMembershipCreditNameForItem,
    booleanFilter,
    getMembershipDateRange,
    contentUtils
} from '~/shared/utils';
import { Types, Fragments } from '~/graphql';
import { type Membership, PartialBy } from '~/core/types/types';
import { EventTransformers } from '~/shared/transformers';
import { formatTranslation } from '~/shared/formatters';
import { WebTracking } from '.';
import { type ProductAdded } from './web/segment';
import { createUserFullName } from '~/shared/hooks';
import { IntlShape } from 'react-intl';
import { DateFormat, parseDBDate } from 'components';

import type {
    Event,
    EventContent,
    EventItemsSelectionItem
} from '~/shared/utils/booking-utils/types';
import { getTicketPrice } from '~/shared/utils/booking-utils/common.utils';

import dayjs from 'dayjs';
import _uniq from 'lodash/uniq';

export const transformMembershipToProduct = (
    membership: PartialBy<Membership, 'membershipType'>
): ProductAdded => ({
    category: 'MEMBERSHIP',
    variant: membership.membershipType || 'ONLINE',
    price: membership.price / 100,
    location: membership.country,
    sku: membership.cycle,
    specialties: [],
    main_speciality: '',
    name: formatTranslation(membership.name, { locale: 'de-DE' }) || ''
});

export const transformTopUpToProduct = (props: {
    topUp: Types.MembershipItem;
    locale: Locale;
    buyLink?: string;
    index?: number;
    membership: Membership;
}): ProductAdded => {
    const { topUp, locale, index, buyLink, membership } = props;
    const accreditationPointsName = getMembershipCreditNameForItem(topUp, locale);
    const sku = `${topUp.amount}`;

    return {
        category: 'TOPUP',
        variant: membership.membershipType || 'ONLINE',
        sku,
        price: typeof topUp.price === 'number' ? topUp.price / 100 : -1,
        location: topUp.country,
        specialties: [],
        ...(buyLink ? { url: buyLink } : undefined),
        ...(typeof index === 'number' ? { position: index + 1 } : undefined),
        name: `TopUp | +${topUp.amount} ${accreditationPointsName}`,
        main_speciality: ''
    };
};

export const transformUserForIdentifying = (props: {
    user: Fragments.UserFieldsFragment;
    userMembership: Fragments.UserMembershipFieldsFragment | null;
    intl: IntlShape;
}): {
    academic_degree_postfix?: string;
    academic_degree_prefix?: string;
    addresses: Array<{
        apartment_number?: string;
        city?: string;
        country?: string;
        full_name?: string;
        is_personal?: boolean;
        postal_code?: string;
        street?: string;
    }>;
    company_id?: string;
    country?: string;
    department?: string;
    email: string;
    facebook_url?: string;
    field_of_activity?: 'CLINIC_DOCTOR' | 'OTHER' | 'SETTLED_DOWN';
    first_name?: string;
    gender?: 'FEMALE' | 'MALE';
    hcp_accepted?: boolean;
    indication?: string;
    last_name?: string;
    linkedin_url?: string;
    medical_identity_number?: string;
    membership?: string;
    // DateTime
    membership_end?: string;
    // DateTime
    membership_start?: string;
    mobile_phone_number?: string;
    occupation?: string;
    occupation_type?: Types.OccupationType;
    phone_number?: string;
    phone_number_private?: string;
    position?: string;
    salutation?: string;
    specialities?: Array<string>;
    specializations?: Array<string>;
    state?: string;
    ub_account_code?: string;
    ub_account_type?: string;
    user_id: string;
} => {
    const { user, userMembership, intl } = props;
    const { locale } = intl;
    const academicDegree = user.academicDegree?.description.find((item) => item.locale === locale);

    const academicDegreePrefix =
        academicDegree && !academicDegree.isSuffix ? academicDegree.value : undefined;
    const academicDegreePostfix =
        academicDegree && academicDegree.isSuffix ? academicDegree.value : undefined;

    const fullName = createUserFullName(user, intl);

    return {
        ...(academicDegreePrefix ? { academic_degree_prefix: academicDegreePrefix } : {}),
        ...(academicDegreePostfix ? { academic_degree_postfix: academicDegreePostfix } : {}),
        addresses: (user.addresses || []).filter(booleanFilter).map((address) => ({
            ...(address.apartmentNumber
                ? { apartment_number: address.apartmentNumber }
                : undefined),

            ...(address.city ? { city: address.city } : undefined),

            ...(address.country ? { country: address.country } : undefined),

            ...(address.fullName ? { full_name: address.fullName } : undefined),

            ...(address.isPersonal ? { is_personal: address.isPersonal } : undefined),

            ...(address.postalCode ? { postal_code: address.postalCode } : undefined),

            ...(address.street ? { street: address.street } : undefined)
        })),
        ...(user.company ? { company_id: user.company.id } : undefined),
        ...(user.country ? { country: user.country } : undefined),
        email: user.email,
        ...(user.fieldOfActivity ? { country: user.fieldOfActivity } : undefined),
        ...(user.firstName ? { first_name: user.firstName } : undefined),
        ...(user.gender ? { gender: user.gender } : undefined),
        ...(user.healthProfessionalConfirmation
            ? { hcp_accepted: user.healthProfessionalConfirmation }
            : undefined),
        ...(user.lastName ? { last_name: user.lastName } : undefined),
        ...(user.medicalIdentificationNumber
            ? { medical_identity_number: user.medicalIdentificationNumber }
            : undefined),
        ...(userMembership && userMembership.expiresAt
            ? {
                  membership: formatTranslation(userMembership.membership.name, { locale }) || '',
                  membership_type: userMembership.membership.membershipType,
                  membership_end: userMembership.expiresAt || '',
                  membership_start: getMembershipDateRange(
                      userMembership.expiresAt,
                      userMembership.cycle,
                      userMembership.initialStartedAt,
                      userMembership.startedAt
                  ).startDate.format(DateFormat.db)
              }
            : {
                  // passing 'none' value so in GA4 we have distinction between anonymous users and logged-in users without membership
                  membership: 'none',
                  membership_type: 'none',
                  membership_start: 'none',
                  membership_end: 'none'
              }),
        ...(user.mobile ? { mobile_phone_number: user.mobile } : undefined),
        ...(user.occupationType ? { occupation_type: user.occupationType } : undefined),
        ...(fullName ? { salutation: fullName } : undefined),
        ...(user.specialities.length > 0
            ? { specialities: user.specialities.filter(booleanFilter).map((item) => item.code) }
            : undefined),
        ...(user.ubAccountCode ? { ub_account_code: user.ubAccountCode } : undefined),
        ...(user.accountType ? { ub_account_type: user.accountType } : undefined),

        user_id: user.id
    };
};

export const getContentNameForTracking = (
    contentForAnalytics: Pick<
        Fragments.ContentForAnalyticsFieldsFragment,
        'brand' | 'city' | 'product' | 'specialities' | 'startDateTime'
    >,
    locale: Locale
) => {
    const brand = contentForAnalytics.brand || 'UPDATE_REFRESHER';

    const city = contentForAnalytics.city as Record<Locale, string> | null;
    const cityTitle = formatTranslation(city, { locale }) || '';

    const speciality = contentForAnalytics.product?.code || contentForAnalytics.specialities[0];
    const date = parseDBDate(contentForAnalytics.startDateTime).format('MM.YY');
    const name = [speciality, brand, cityTitle, date].filter(booleanFilter).join(' ');
    return name;
};

export const transformPrintedCoursebookToProduct = (
    event: Event,
    participationType: Types.ParticipationType
): WebTracking.ProductAdded => ({
    ...(event.content?.contentId ? { content_id: event.content.contentId } : undefined),
    brand: EventTransformers.transformEventFormatToContentBrand(event.eventFormat),
    category: 'COURSE',
    variant: participationType === Types.ParticipationType.Livestream ? 'ONLINE' : 'ON_SITE',
    sku: Types.TicketType.PrintedCourseBook,
    location: event.city?.value || '',
    price:
        typeof event.printedCourseBookPrice === 'number' ? event.printedCourseBookPrice / 100 : -1,
    specialties: [],
    main_speciality: event.productCategory.code,
    name: 'Printed Course Book'
});

export const transformEventItemToProduct = (
    event: Event,
    eventItem: EventItemsSelectionItem,
    content: EventContent | undefined,
    locale: Locale
): WebTracking.ProductAdded => {
    const startDateTime = dayjs(eventItem.startDateTime).toDate();
    const endDateTime = dayjs(eventItem.endDateTime).toDate();

    return {
        ...(event.content?.contentId ? { content_id: event.content.contentId } : undefined),
        brand: EventTransformers.transformEventFormatToContentBrand(event.eventFormat),
        category: 'COURSE',
        variant:
            eventItem.participationType === Types.ParticipationType.Livestream
                ? 'ONLINE'
                : 'ON_SITE',
        sku: eventItem.ticketType,
        location: event.city?.value || '',
        price: eventItem.price / 100,
        specialties: [],
        main_speciality: event.productCategory.code,
        ...(content
            ? {
                  name: getContentNameForTracking(content, locale),
                  original_title: content.title || ''
              }
            : {}),
        start_datetime: startDateTime,
        end_datetime: endDateTime
    };
};

export const transformEventToProduct = (
    event: Event,
    content: EventContent,
    selectedItems: Array<EventItemsSelectionItem>,
    totalPrice: number,
    locale: Locale
): WebTracking.ProductAdded => {
    const selectedItem = selectedItems[0];
    return {
        ...(event.content?.contentId ? { content_id: event.content.contentId } : undefined),
        brand: EventTransformers.transformEventFormatToContentBrand(event.eventFormat),
        category: 'COURSE',
        variant:
            selectedItem?.participationType === Types.ParticipationType.Livestream
                ? 'ONLINE'
                : 'ON_SITE',
        sku: selectedItem?.ticketType || '',
        location: event.city?.value || '',
        price: totalPrice / 100,

        // hard coding it because we don't have specialities data on BookingEventR
        specialties: [],
        main_speciality: event.productCategory.code,
        name: getContentNameForTracking(content, locale)
    };
};

export const transformSelectedItemToProduct = (
    event: Event,
    selectedItem: EventItemsSelectionItem,
    content: EventContent | undefined,
    locale: Locale
): WebTracking.ProductAdded => {
    const startDateTime = dayjs(selectedItem.startDateTime).toDate();
    const endDateTime = dayjs(selectedItem.endDateTime).toDate();

    return {
        ...(event.content?.contentId ? { content_id: event.content.contentId } : undefined),
        brand: EventTransformers.transformEventFormatToContentBrand(event.eventFormat),
        category: 'COURSE',
        variant:
            selectedItem.participationType === Types.ParticipationType.Livestream
                ? 'ONLINE'
                : 'ON_SITE',
        sku: selectedItem.ticketType,
        location: event.city?.value || '',
        price: getTicketPrice(selectedItem) / 100,

        // hard coding it because we don't have specialities data on BookingEventR
        specialties: [],
        main_speciality: event.productCategory.code,
        ...(content ? { name: getContentNameForTracking(content, locale) } : undefined),
        end_datetime: endDateTime,
        start_datetime: startDateTime
    };
};

export const transformToOrderStarted = ({
    event,
    totalPrice,
    currency,
    category,
    main_speciality,
    products
}: {
    event?: Event;
    products: Array<WebTracking.OrderCompletedProduct>;
    currency: Types.Currency;
    totalPrice: number;
    main_speciality?: string;
    category: WebTracking.CategoryElement;
}): WebTracking.OrderStarted => ({
    ...(event
        ? { brand: EventTransformers.transformEventFormatToContentBrand(event.eventFormat) }
        : undefined),
    category,
    main_speciality,
    total: totalPrice / 100,
    currency,
    products
});

export const transformToCompletedOrder = ({
    event,
    bookingId,
    category,
    clientReference,
    coupon,
    currency,
    discount,
    locale,
    main_speciality,
    products,
    specialities,
    tax,
    totalPrice
}: {
    event?: Event;
    bookingId: string;
    category: WebTracking.CategoryElement;
    clientReference: string;
    coupon?: string;
    currency: Types.Currency;
    discount?: number;
    locale: Locale;
    main_speciality?: string | undefined;
    products: Array<WebTracking.OrderCompletedProduct>;
    specialities?: Array<string>;
    tax?: number;
    totalPrice: number;
}): WebTracking.OrderCompleted => ({
    ...(event
        ? { brand: EventTransformers.transformEventFormatToContentBrand(event.eventFormat) }
        : undefined),
    category,
    coupon,
    currency,
    discount: typeof discount === 'number' ? discount / 100 : discount,
    locale,
    main_speciality,
    order_id: bookingId,
    products,
    specialities,
    tax,
    total: totalPrice / 100,
    transaction_id: clientReference
});

export const transformUserForSignUpStep = (
    user: Pick<
        Fragments.UserFieldsFragment,
        'email' | 'firstName' | 'lastName' | 'specialities' | 'addresses' | 'id'
    >,
    step: WebTracking.SignupStepCompletedStep
): WebTracking.SignupStepCompleted => ({
    step
});

export const transformContentDocumentsToProductList = (
    contentsForAnalytics: Array<Fragments.ContentForAnalyticsFieldsFragment>,
    listId: string | undefined,
    locale: Locale
): WebTracking.ProductListViewed => {
    const products = contentsForAnalytics.map((contentForAnalytics, index) => {
        const { specialties, ...product } = transformContentDocumentToProduct(contentForAnalytics, {
            locale,
            index
        });
        return {
            ...product,
            position: index + 1
        };
    });
    const productsCategories = _uniq(products.map((product) => product.category));
    const productsBrands = _uniq(products.map((product) => product.brand)).filter(booleanFilter);
    return {
        list_id: listId,
        brands: productsBrands,
        categories: productsCategories,
        products
    };
};

export const transformContentDocumentToProduct = (
    contentForAnalytics: Fragments.ContentForAnalyticsFieldsFragment,
    params: {
        locale: Locale;
        index: number | undefined;
    }
): WebTracking.ProductAdded => {
    const { parent } = contentForAnalytics;
    const { locale, index } = params;
    const content = {
        id: contentForAnalytics.id,
        contentId: contentForAnalytics.contentId,
        brand: contentForAnalytics.brand || parent?.brand,
        city: contentForAnalytics.city || parent?.city,
        contentType: contentForAnalytics.contentType || parent?.contentType,
        specialities: contentForAnalytics.specialities || parent?.specialities,
        product: contentForAnalytics.product || parent?.product,
        title: contentForAnalytics.title || parent?.title,
        partners: (contentForAnalytics.partnerships || parent?.partnerships || [])
            .map((partnership) => partnership.partnerCompany)
            .filter(booleanFilter),
        startDateTime: parent?.startDateTime || contentForAnalytics.startDateTime
    };

    const city = content.city as Record<Locale, string> | null;

    const isVideo = contentUtils.isVideoContent(content);
    const isDocument = contentUtils.isDocumentContent(content);

    const brand = content.brand || Types.ProductBrand.UpdateRefresher;
    const category = (() => {
        if (isDocument) {
            return 'DOCUMENT';
        } else if (isVideo) {
            return 'VIDEO';
        } else if (content.contentType === Types.ContentType.Webinar) {
            return 'WEBINAR';
        } else {
            return 'COURSE';
        }
    })();

    return {
        content_id: content.contentId,
        brand: brand,
        category,
        location: (city && city[locale]) || '',
        // setting it to -1 since we don't really need the price for content viewed or content added events, only for events on booking page
        price: -1,
        variant: 'HYBRID',
        specialties: content.specialities,
        main_speciality: content.product?.code || '',
        name: getContentNameForTracking(content, locale),
        original_title: content.title || '',
        has_partnership: content.partners.length > 0,
        partnerships: content.partners.map((partner) => ({
            partner_ub_account_code: partner.ubAccountCode || undefined,
            partner_name: partner.name || undefined
        })),
        ...(typeof index === 'number' ? { position: index + 1 } : undefined)
    };
};

export const transformCourseDocumentToProduct = (
    { parent, ...courseForAnalytics }: Fragments.CourseForAnalyticsFieldsFragment,
    params: {
        locale: Locale;
        index: number | undefined;
    }
): WebTracking.ProductAdded => {
    const partnerships = [] as Fragments.ContentForAnalyticsFieldsFragment['partnerships'];
    const courseToContent = {
        ...courseForAnalytics,
        partnerships,
        ...(parent
            ? {
                  parent: {
                      ...parent,
                      partnerships
                  }
              }
            : undefined)
    };

    return transformContentDocumentToProduct(courseToContent, params);
};

export type PlaybackTrackingParams = {
    contentId: string;
    contentIds: Array<string>;
    // playback position in seconds
    playbackPosition: number;
    playbackProgress: number;
    sessionId: Types.UUID;
    // value from 0 to 1
    volume: number;
    duration: number;
};

export const transformToPlayback = (
    params: PlaybackTrackingParams,
    isPlaybackStartedEvent: boolean
): Pick<WebTracking.VideoPlaybackStarted, 'video_playback'> => {
    const {
        contentId,
        contentIds,
        playbackPosition,
        playbackProgress,
        sessionId,
        volume,
        duration
    } = params;
    return {
        video_playback: {
            ...(isPlaybackStartedEvent
                ? { content_asset_ids: contentIds }
                : { content_asset_id: contentId }),
            position: Math.round(playbackPosition),
            session_id: sessionId,
            sound: volume * 100,
            total_length: Math.round(duration),
            progress: playbackProgress
        }
    };
};

export const transformPartnerCompany = (
    company: Fragments.PartnerCompanySimplifiedFragment,
    params: {
        locale: Locale;
        index: number | undefined;
    }
): WebTracking.PartnerLogoViewed => ({
    partnerCompany: {
        id: company.id,
        slug: company.slug || undefined,
        name: company.name || undefined,
        index: params.index
    }
});
